import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSettings } from "@/context/SettingsContext";
import { FIBER_HEX, fiberHexStd, HEX_BY_NAME, splOutputs, exportPng } from "./splice/spliceUtils";
import { colorsForStandard } from "@/lib/modules";
import { SpliceColumns } from "./splice/SpliceColumns";
import { SpliceSplitters } from "./splice/SpliceSplitters";

export default function SpliceMap({ closureId, closureNumber, readOnly = false, planMode = false, planned = [], onPlanAdd, onPlanRemove }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const [cables, setCables] = useState([]);
  const [splices, setSplices] = useState([]);
  const [splitters, setSplitters] = useState([]);
  const [clients, setClients] = useState([]);
  const [layout, setLayout] = useState({});
  const [armed, setArmed] = useState(null);
  const [armedSpl, setArmedSpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selA, setSelA] = useState("");
  const [selB, setSelB] = useState("");
  const [xA, setXA] = useState({ cable: "", fiber: "" });
  const [xB, setXB] = useState({ cable: "", fiber: "" });
  const [trace, setTrace] = useState(null);
  const svgRef = useRef(null);
  const traceReq = useRef(0);

  const load = useCallback(async () => {
    const [cs, sp, sl, cc, lay, cli] = await Promise.all([
      api.get("/cables"), api.get("/splices"), api.get("/splitters"),
      api.get(`/closures/${closureId}/cables`).catch(() => ({ data: {} })),
      api.get(`/closures/${closureId}/splice-layout`).catch(() => ({ data: { cables: [] } })),
      api.get("/clients").catch(() => ({ data: [] })),
    ]);
    const rel = new Set([
      ...((cc.data && cc.data.cables) || []).map((x) => x.id),
      ...((cc.data && cc.data.nearby) || []).map((x) => x.id),
      ...((cc.data && cc.data.pass_through_ids) || []),
    ]);
    const relSplices = (sp.data || []).filter((s) => s.closure_id === closureId);
    const spCables = new Set();
    relSplices.forEach((s) => { if (s.cable_id) spCables.add(s.cable_id); if (s.cable_out_id) spCables.add(s.cable_out_id); });
    setCables((cs.data || []).filter((c) => rel.has(c.id) || spCables.has(c.id) || c.closure_a_id === closureId || c.closure_b_id === closureId));
    setSplices(relSplices);
    setSplitters((sl.data || []).filter((s) => s.closure_id === closureId));
    const clSpl = new Set((sl.data || []).filter((s) => s.closure_id === closureId).map((s) => s.id));
    setClients((cli.data || []).filter((c) => c.splitter_id && clSpl.has(c.splitter_id)));
    const lmap = {};
    ((lay.data && lay.data.cables) || []).forEach((x) => { lmap[x.cable_id] = x; });
    setLayout(lmap);
  }, [closureId]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const isOcc = (cable, n) => { const f = (cable.fibers || []).find((x) => Number(x.number) === n); return f && (f.status === "zajęte" || f.status === "zajete"); };
  const freeFibers = (cable) => {
    if (!cable) return [];
    const cnt = Number(cable.fiber_count || 12);
    return Array.from({ length: cnt }, (_, k) => k + 1).filter((n) => !isOcc(cable, n));
  };

  const clickFiber = async (cable, n, side) => {
    if (readOnly || busy) return;
    if (planMode) {
      if (!armed) { setArmed({ cableId: cable.id, fiber: n, number: cable.number, side }); return; }
      if (armed.cableId === cable.id && armed.fiber === n) { setArmed(null); return; }
      onPlanAdd && onPlanAdd({ cable_in_id: armed.cableId, cable_in_number: armed.number, fiber_in: armed.fiber, cable_out_id: cable.id, cable_out_number: cable.number, fiber_out: n });
      setArmed(null); return;
    }
    if (!armed) { setArmed({ cableId: cable.id, fiber: n, number: cable.number, side }); return; }
    if (armed.cableId === cable.id && armed.fiber === n && armed.side === side) { setArmed(null); return; }
    setBusy(true);
    try {
      await api.post(`/closures/${closureId}/splice-manual`, {
        cable_in_id: armed.cableId, fiber_in: armed.fiber, side_in: armed.side || null,
        cable_out_id: cable.id, fiber_out: n, side_out: side || null,
      });
      toast.success(`${t("Zespawano")}: ${armed.number}/${armed.fiber} ↔ ${cable.number}/${n}`);
      setArmed(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const clickSplitterPort = async (spl, dir, port) => {
    if (readOnly || planMode || busy) return;
    if (armed) {
      const occSplice = splices.find((s) => s.target_splitter_id === spl.id
        && (s.splitter_dir || "in") === dir && Number(s.splitter_port ?? 0) === Number(port));
      if (occSplice) { toast.info(dir === "in" ? t("Wejście splittera jest już zajęte") : t("Ten port wyjściowy splittera jest już zajęty")); return; }
      setBusy(true);
      try {
        await api.post(`/closures/${closureId}/splice-manual`, { cable_in_id: armed.cableId, fiber_in: armed.fiber, side_in: armed.side || null, target_splitter_id: spl.id, splitter_dir: dir, splitter_port: port });
        toast.success(`${t("Zespawano do splittera")}: ${armed.number}/${armed.fiber} → ${spl.number}`);
        setArmed(null); await load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
      return;
    }
    if (!armedSpl) {
      if (dir === "out") setArmedSpl({ id: spl.id, port, number: spl.number });
      else toast.info(t("Kliknij port WYJŚCIOWY splittera, potem WEJŚCIE (WE) drugiego splittera"));
      return;
    }
    if (armedSpl.id === spl.id) { setArmedSpl(null); return; }
    if (dir !== "in") { toast.info(t("Kliknij WEJŚCIE (WE) drugiego splittera")); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/splitters/${armedSpl.id}/connect-child-port`, { child_id: spl.id, parent_port: armedSpl.port });
      toast.success(`${t("Połączono splittery")}: ${armedSpl.number} → ${spl.number}${r.pon_port ? ` · PON ${r.pon_port}` : ""}`);
      setArmedSpl(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const delSplice = async (s) => {
    if (readOnly || busy || !window.confirm(t("Rozłączyć ten spaw?"))) return;
    setBusy(true);
    try { await api.delete(`/closures/splice/${s.id}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const cutOne = async (cableId, n) => {
    if (readOnly || busy) return;
    setBusy(true);
    try {
      let r = await api.post(`/closures/${closureId}/cut-fiber`, { cable_id: cableId, fiber: n });
      if (r.data && r.data.ok === false && r.data.occupied) {
        if (!window.confirm(r.data.detail || t("Włókno jest zajęte. Przeciąć?"))) { setBusy(false); return; }
        r = await api.post(`/closures/${closureId}/cut-fiber`, { cable_id: cableId, fiber: n, confirm: true });
      }
      toast.success(`${t("Przecięto włókno")} ${n}`); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const joinOne = async (cableId, n) => {
    if (readOnly || busy) return;
    setBusy(true);
    try { await api.post(`/closures/${closureId}/uncut-fiber`, { cable_id: cableId, fiber: n }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cutAll = async (cableId, num) => {
    if (busy || !window.confirm(`${t("Przeciąć wszystkie włókna kabla")} ${num}?`)) return;
    setBusy(true);
    try { await api.post(`/closures/${closureId}/cut-fiber`, { cable_id: cableId, all: true }); toast.success(t("Przecięto cały kabel")); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const joinAll = async (cableId) => {
    if (busy) return;
    setBusy(true);
    try { await api.post(`/closures/${closureId}/uncut-fiber`, { cable_id: cableId, all: true }); toast.success(t("Przywrócono ciągłość")); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const autoSplice = async () => {
    if (busy || !selA || !selB || selA === selB) { toast.info(t("Wybierz dwa różne kable")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/closures/${closureId}/splice-auto`, { cable_a_id: selA, cable_b_id: selB });
      toast.success(`${t("Zespawano 1:1")}: ${data.created} ${t("włókien")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // Spaw krzyżowy: wskazane wolne włókno A ↔ wskazane wolne włókno B
  const crossSplice = async () => {
    if (busy) return;
    if (!xA.cable || !xB.cable || !xA.fiber || !xB.fiber) { toast.info(t("Wybierz kabel i wolne włókno po obu stronach")); return; }
    if (xA.cable === xB.cable && Number(xA.fiber) === Number(xB.fiber)) { toast.info(t("Wybierz różne włókna")); return; }
    setBusy(true);
    try {
      await api.post(`/closures/${closureId}/splice-manual`, {
        cable_in_id: xA.cable, fiber_in: Number(xA.fiber),
        cable_out_id: xB.cable, fiber_out: Number(xB.fiber),
      });
      const na = cables.find((c) => c.id === xA.cable)?.number;
      const nb = cables.find((c) => c.id === xB.cable)?.number;
      toast.success(`${t("Zespawano krzyżowo")}: ${na}/${xA.fiber} ↔ ${nb}/${xB.fiber}`);
      setXA({ cable: "", fiber: "" }); setXB({ cable: "", fiber: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // Dymek: trasa włókna w stronę OLT
  const onFiberHover = async (cable, n, e) => {
    const rid = ++traceReq.current;
    setTrace({ loading: true, x: e.clientX, y: e.clientY, title: `${cable.number}/${n}`, steps: [], total: null });
    try {
      const { data } = await api.get(`/cables/${cable.id}/fiber-trace`, { params: { fiber: n } });
      if (rid !== traceReq.current) return;
      setTrace({ loading: false, x: e.clientX, y: e.clientY, title: `${cable.number}/${n}`, steps: data.steps || [], total: data.total_atten_db,
        budget: data.budget_db, remaining: data.remaining_db, status: data.budget_status });
    } catch { if (rid === traceReq.current) setTrace(null); }
  };
  const onFiberLeave = () => { traceReq.current++; setTrace(null); };

  // --- model kolumn: kabel przelotowy = 2 kolumny (A=wejście / B=wyjście) ---
  const columns = [];
  cables.forEach((c) => {
    const lay = layout[c.id] || {};
    if (lay.pass_through) {
      columns.push({ cableId: c.id, cable: c, side: "A", end: lay.end_a });
      columns.push({ cableId: c.id, cable: c, side: "B", end: lay.end_b });
    } else {
      columns.push({ cableId: c.id, cable: c, side: null, end: lay.end });
    }
  });
  const colX = (i) => 80 + i * 160;
  const fiberY = (n) => 70 + (n - 1) * 16;
  const colIndexOf = (cableId, side) => {
    let i = columns.findIndex((col) => col.cableId === cableId && col.side === side);
    if (i < 0) i = columns.findIndex((col) => col.cableId === cableId);
    return i;
  };
  const nodePos = (cableId, fiber, side) => {
    const i = colIndexOf(cableId, side);
    return i < 0 ? null : { x: colX(i), y: fiberY(fiber) };
  };
  const maxF = Math.max(12, ...cables.map((c) => Number(c.fiber_count || 12)));
  const splX = (i) => colX(columns.length) + 30 + i * 90;
  const splInY = 70; const splOutY = (p) => 104 + p * 16;
  const height = Math.max(fiberY(maxF), splOutY(Math.max(0, ...splitters.map(splOutputs)))) + 30;
  const width = Math.max(360, columns.length * 160 + splitters.length * 90 + 140);
  const splById = {}; splitters.forEach((s, i) => { splById[s.id] = { s, x: splX(i) }; });
  const splPortOcc = {};
  splices.forEach((sp) => {
    if (sp.target_splitter_id) splPortOcc[`${sp.target_splitter_id}-${sp.splitter_dir || "in"}-${Number(sp.splitter_port ?? 0)}`] = sp;
  });
  splitters.forEach((ch) => {
    if (ch.parent_splitter_id && ch.parent_port != null && ch.parent_port !== "") {
      splPortOcc[`${ch.parent_splitter_id}-out-${Number(ch.parent_port)}`] = { cascade: true, childNumber: ch.number };
    }
  });
  clients.forEach((c) => {
    if (c.splitter_id && c.splitter_port != null && c.splitter_port !== "") {
      const key = `${c.splitter_id}-out-${Number(c.splitter_port)}`;
      if (!splPortOcc[key]) splPortOcc[key] = { client: true, clientName: c.name };
    }
  });
  const splicedFib = new Set();
  splices.forEach((s) => {
    if (s.cable_id != null && s.fiber_in != null) splicedFib.add(`${s.cable_id}-${s.fiber_in}`);
    if (s.cable_out_id != null && s.fiber_out != null) splicedFib.add(`${s.cable_out_id}-${s.fiber_out}`);
  });
  const passCables = cables.filter((c) => (layout[c.id] || {}).pass_through);

  const dirText = (end, arrow) => {
    if (!end) return "";
    const nm = end.label || "";
    const olt = end.olt ? ` (OLT: ${end.olt})` : "";
    if (!nm && !olt) return "";
    return arrow === "left" ? `⟵ ${nm}${olt}` : `${nm}${olt} ⟶`;
  };

  const traceIcon = { cable: "—", splice: "•", splitter: "▤", olt: "◉", end: "×", info: "!" };

  return (
    <div className="border-t border-slate-700 pt-3 mt-1" data-testid="splice-map">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <span className="text-xs text-cyan-400 font-semibold uppercase tracking-wide">{t("Rozpływ włókien (interaktywny)")}</span>
        <div className="flex items-center gap-2">
          {cables.length > 0 && (
            <button type="button" onClick={() => exportPng(svgRef.current, closureId, closureNumber, toast, t, colorsForStandard(settings, settings?.default_fiber_standard))} data-testid="splice-png-btn"
              title={t("Pobierz kolorowy schemat spawów jako obrazek PNG")}
              className="px-2 py-0.5 rounded border border-slate-600 bg-slate-800 hover:bg-slate-700 text-[11px] text-slate-200">
              {t("Pobierz PNG")}
            </button>
          )}
          <span className="text-[11px] text-slate-400">{readOnly ? t("Podgląd rozpływu (tylko odczyt)") : planMode ? (armed ? `${t("Wybrano")}: ${armed.number}/${armed.fiber} — ${t("kliknij drugie włókno, aby zaplanować spaw")}` : t("Kliknij dwa włókna, aby zaplanować spaw · kliknij zaplanowany spaw, aby usunąć")) : armed ? `${t("Wybrano")}: ${armed.number}/${armed.fiber} — ${t("kliknij drugie włókno lub port splittera")}` : armedSpl ? `${t("Splitter")} ${armedSpl.number} ${t("port")} ${armedSpl.port} — ${t("kliknij WE drugiego splittera")}` : t("Włókno → drugie/splitter · lub port WY splittera → WE drugiego")}</span>
        </div>
      </div>
      {!readOnly && !planMode && passCables.length > 0 && (
        <div className="flex items-center gap-2 mb-2 text-[11px] flex-wrap" data-testid="passthrough-bar">
          <span className="text-slate-400">{t("Kable przelotowe (WE→WY)")}:</span>
          {passCables.map((c) => {
            const cut = ((layout[c.id] || {}).cut_fibers || []).length;
            return (
              <span key={c.id} className="inline-flex items-center gap-1 bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5">
                <span className="text-slate-200 font-semibold">{c.number}</span>
                <button type="button" onClick={() => cutAll(c.id, c.number)} disabled={busy} data-testid={`cut-all-${c.id}`}
                  className="px-1.5 py-0.5 rounded bg-rose-700 hover:bg-rose-600 text-white disabled:opacity-50">{t("Przetnij cały")}</button>
                <button type="button" onClick={() => joinAll(c.id)} disabled={busy || !cut} data-testid={`join-all-${c.id}`}
                  className="px-1.5 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-40">{t("Połącz cały")}</button>
              </span>
            );
          })}
        </div>
      )}
      {!readOnly && !planMode && cables.length >= 2 && (
        <div className="flex items-center gap-2 mb-2 text-xs flex-wrap" data-testid="auto-splice-bar">
          <select value={selA} onChange={(e) => setSelA(e.target.value)} data-testid="auto-cable-a" className="bg-slate-900 border border-slate-700 rounded px-2 py-1">
            <option value="">{t("Kabel A")}</option>{cables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
          </select>
          <span className="text-slate-400">↔</span>
          <select value={selB} onChange={(e) => setSelB(e.target.value)} data-testid="auto-cable-b" className="bg-slate-900 border border-slate-700 rounded px-2 py-1">
            <option value="">{t("Kabel B")}</option>{cables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
          </select>
          <button type="button" onClick={autoSplice} disabled={busy} data-testid="auto-splice-btn" className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white">{t("Zespawaj wolne 1:1")}</button>
        </div>
      )}
      {!readOnly && !planMode && cables.length >= 1 && (
        <div className="flex items-center gap-1.5 mb-2 text-xs flex-wrap bg-slate-900/50 border border-slate-700 rounded px-2 py-1.5" data-testid="cross-splice-bar">
          <span className="text-slate-400 mr-1">{t("Spaw krzyżowy")}:</span>
          <select value={xA.cable} onChange={(e) => setXA({ cable: e.target.value, fiber: "" })} data-testid="cross-cable-a" className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1">
            <option value="">{t("Kabel A")}</option>{cables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
          </select>
          <select value={xA.fiber} onChange={(e) => setXA((s) => ({ ...s, fiber: e.target.value }))} disabled={!xA.cable} data-testid="cross-fiber-a" className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 disabled:opacity-40">
            <option value="">{t("wł.")}</option>{freeFibers(cables.find((c) => c.id === xA.cable)).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-slate-400">↔</span>
          <select value={xB.cable} onChange={(e) => setXB({ cable: e.target.value, fiber: "" })} data-testid="cross-cable-b" className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1">
            <option value="">{t("Kabel B")}</option>{cables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
          </select>
          <select value={xB.fiber} onChange={(e) => setXB((s) => ({ ...s, fiber: e.target.value }))} disabled={!xB.cable} data-testid="cross-fiber-b" className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 disabled:opacity-40">
            <option value="">{t("wł.")}</option>{freeFibers(cables.find((c) => c.id === xB.cable)).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button type="button" onClick={crossSplice} disabled={busy} data-testid="cross-splice-btn" className="px-2.5 py-1 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white">{t("Połącz")}</button>
        </div>
      )}
      {!cables.length ? (
        <p className="text-[11px] text-slate-500">{t("Brak kabli podłączonych do tej mufy (ustaw Mufa A/B w kablu).")}</p>
      ) : (
        <div className="overflow-auto bg-slate-900/60 rounded border border-slate-700" style={{ maxHeight: 380 }}>
          <svg ref={svgRef} width={width} height={height} data-testid="splice-svg">
            {passCables.map((c) => {
              const lay = layout[c.id] || {};
              const cut = new Set(lay.cut_fibers || []);
              const cnt = Number(c.fiber_count || 12);
              const items = [];
              for (let n = 1; n <= cnt; n++) {
                if (splicedFib.has(`${c.id}-${n}`)) continue;
                const a = nodePos(c.id, n, "A"); const b = nodePos(c.id, n, "B");
                if (!a || !b) continue;
                if (cut.has(n)) {
                  items.push(
                    <g key={`cut-${c.id}-${n}`} onClick={() => joinOne(c.id, n)} style={{ cursor: "pointer" }} data-testid={`cut-line-${c.id}-${n}`}>
                      <title>{t("Przecięte — kliknij, aby przywrócić ciągłość")}</title>
                      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#475569" strokeWidth="1" strokeDasharray="2 4" />
                    </g>
                  );
                } else {
                  items.push(
                    <g key={`cont-${c.id}-${n}`} onClick={() => cutOne(c.id, n)} style={{ cursor: "pointer" }} data-testid={`continuity-${c.id}-${n}`}>
                      <title>{c.number}/{n} — {t("ciągłość (kliknij, aby przeciąć)")}</title>
                      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={fiberHexStd(settings, c, n)} strokeWidth="2" />
                    </g>
                  );
                }
              }
              return <g key={`pass-${c.id}`}>{items}</g>;
            })}
            {splices.map((s) => {
              const a = nodePos(s.cable_id, s.fiber_in, s.side_in || "A");
              let b = nodePos(s.cable_out_id, s.fiber_out, s.side_out || "A");
              if (!b && s.target_splitter_id && splById[s.target_splitter_id]) {
                const sx = splById[s.target_splitter_id].x;
                b = (s.splitter_dir === "out" && s.splitter_port)
                  ? { x: sx, y: splOutY(Number(s.splitter_port)) }
                  : { x: sx, y: splInY };
              }
              if (!a || !b) return null;
              const mx = (a.x + b.x) / 2; const col = fiberHexStd(settings, cables.find((x) => x.id === s.cable_id), s.fiber_in);
              return (
                <g key={s.id} onClick={() => delSplice(s)} style={{ cursor: "pointer" }} data-testid={`splice-${s.id}`}>
                  <path d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`} stroke={col} strokeWidth="2" fill="none" />
                  <ellipse cx={mx} cy={(a.y + b.y) / 2} rx="7" ry="4.5" stroke={col} strokeWidth="1.2" fill="none" />
                </g>
              );
            })}
            {planMode && (planned || []).map((p, idx) => {
              const a = nodePos(p.cable_in_id, p.fiber_in, "A");
              const b = nodePos(p.cable_out_id, p.fiber_out, "A");
              if (!a || !b) return null;
              const mx = (a.x + b.x) / 2;
              return (
                <g key={`plan-${idx}`} onClick={() => onPlanRemove && onPlanRemove(idx)} style={{ cursor: "pointer" }} data-testid={`planned-splice-${idx}`}>
                  <title>{t("Zaplanowany spaw — kliknij, aby usunąć")}</title>
                  <path d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`} stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="5 3" fill="none" />
                  <ellipse cx={mx} cy={(a.y + b.y) / 2} rx="7" ry="4.5" stroke="#f59e0b" strokeWidth="1.4" fill="#1e293b" />
                </g>
              );
            })}
            {splitters.map((ch) => {
              if (!ch.parent_splitter_id || ch.parent_port == null || ch.parent_port === "") return null;
              const parent = splById[ch.parent_splitter_id]; const child = splById[ch.id];
              if (!parent || !child) return null;
              const a = { x: parent.x, y: splOutY(Number(ch.parent_port)) };
              const b = { x: child.x, y: splInY }; const mx = (a.x + b.x) / 2;
              return (
                <g key={`casc-${ch.id}`} data-testid={`cascade-${ch.id}`}>
                  <path d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`} stroke="#f97316" strokeWidth="2" strokeDasharray="4 3" fill="none" />
                </g>
              );
            })}
            <SpliceColumns columns={columns} colX={colX} fiberY={fiberY} armed={armed} isOcc={isOcc}
              clickFiber={clickFiber} onFiberHover={onFiberHover} onFiberLeave={onFiberLeave} dirText={dirText} t={t} settings={settings} />
            <SpliceSplitters splitters={splitters} splX={splX} splInY={splInY} splOutY={splOutY}
              splPortOcc={splPortOcc} armedSpl={armedSpl} clickSplitterPort={clickSplitterPort} settings={settings} cables={cables} />
          </svg>
        </div>
      )}
      {trace && (
        <div className="fixed z-[1200] pointer-events-none bg-slate-900 border border-cyan-700/60 rounded-lg shadow-2xl px-3 py-2 text-[11px] w-72"
          style={{ left: Math.min(trace.x + 14, (typeof window !== "undefined" ? window.innerWidth : 1000) - 300), top: Math.min(trace.y + 12, (typeof window !== "undefined" ? window.innerHeight : 700) - 220) }}
          data-testid="fiber-trace-tooltip">
          <div className="text-cyan-300 font-semibold mb-1">{t("Trasa do OLT")} · {trace.title}</div>
          {trace.loading ? (
            <div className="text-slate-400">{t("Ładowanie…")}</div>
          ) : !trace.steps.length ? (
            <div className="text-slate-400">{t("Brak trasy")}</div>
          ) : (
            <>
              <div className="space-y-0.5 max-h-40 overflow-hidden">
                {trace.steps.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="text-slate-200 truncate">
                      <span className="inline-block w-3 text-cyan-400">{traceIcon[s.type] || "·"}</span> {s.name}
                      <span className="text-slate-500"> {s.detail ? `· ${s.detail}` : ""}</span>
                    </span>
                    <span className="text-amber-300 font-mono shrink-0">{s.atten_total} dB</span>
                  </div>
                ))}
              </div>
              <div className="mt-1 pt-1 border-t border-slate-700 flex justify-between text-cyan-200 font-mono">
                <span>{t("Łącznie")}</span><span>{trace.total} dB</span>
              </div>
              {trace.budget != null && (
                <div className={`mt-1 flex items-center justify-between rounded px-1.5 py-1 font-mono ${trace.status === "fail" ? "bg-rose-900/50 text-rose-300" : trace.status === "warn" ? "bg-amber-900/40 text-amber-300" : "bg-emerald-900/40 text-emerald-300"}`}
                  data-testid="fiber-trace-budget">
                  <span className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${trace.status === "fail" ? "bg-rose-400" : trace.status === "warn" ? "bg-amber-400" : "bg-emerald-400"}`} />
                    {trace.status === "fail" ? t("Poza budżetem") : trace.status === "warn" ? t("Budżet napięty") : t("Budżet OK")}
                  </span>
                  <span>{t("zapas")} {trace.remaining} / {trace.budget} dB</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px] text-slate-400" data-testid="fiber-legend">
        {colorsForStandard(settings, settings?.default_fiber_standard).map((nm, i) => (
          <span key={`${nm}-${i}`} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block border border-slate-600" style={{ background: HEX_BY_NAME[nm] || FIBER_HEX[i] }} />{i + 1} {nm}</span>
        ))}
      </div>
    </div>
  );
}
